import React, { useState, useEffect, useReducer, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTableMenu, createOrder } from '../../services/api';

// ── Cart reducer ─────────────────────────────────────────────────────────────
function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const ex = state.find(i => i.id === action.item.id);
      return ex
        ? state.map(i => i.id === action.item.id ? { ...i, qty: i.qty + 1 } : i)
        : [...state, { ...action.item, qty: 1 }];
    }
    case 'REMOVE': {
      const ex = state.find(i => i.id === action.id);
      if (!ex) return state;
      if (ex.qty <= 1) return state.filter(i => i.id !== action.id);
      return state.map(i => i.id === action.id ? { ...i, qty: i.qty - 1 } : i);
    }
    case 'CLEAR': return [];
    default: return state;
  }
}

// ── Category emoji ─────────────────────────────────────────────────────────────
const EMOJI_MAP = [
  ['salat', '🥗'], ['soup', '🍲'], ['şorba', '🍲'], ['çorba', '🍲'],
  ['əsas', '🍖'], ['main', '🍖'], ['pizza', '🍕'], ['burger', '🍔'],
  ['sandviç', '🥪'], ['sandwich', '🥪'], ['pasta', '🍝'], ['makaron', '🍝'],
  ['şirniyyat', '🍰'], ['desert', '🍰'], ['içki', '🥤'], ['drink', '🥤'],
  ['qəhvə', '☕'], ['coffee', '☕'], ['çay', '🫖'], ['tea', '🫖'],
  ['qəlyanaltı', '🍟'], ['snack', '🍟'], ['sushi', '🍱'], ['balıq', '🐟'],
  ['toyuq', '🍗'], ['chicken', '🍗'], ['ət', '🥩'], ['meat', '🥩'],
];
function catEmoji(name) {
  const k = (name || '').toLowerCase();
  for (const [kw, em] of EMOJI_MAP) if (k.includes(kw)) return em;
  return '🍽';
}

// ── Gradient pool ─────────────────────────────────────────────────────────────
const GRADS = [
  'linear-gradient(135deg,#0f2a1a,#071810)',
  'linear-gradient(135deg,#0f1f2a,#071318)',
  'linear-gradient(135deg,#1a0f2a,#100718)',
  'linear-gradient(135deg,#2a1a0f,#181007)',
  'linear-gradient(135deg,#1a2a0f,#101807)',
  'linear-gradient(135deg,#2a0f0f,#180707)',
];
const pickGrad = name => GRADS[name.charCodeAt(0) % GRADS.length];

// ── Tokens ───────────────────────────────────────────────────────────────────
const C = {
  bg:      '#060b14',
  surface: '#0c1525',
  card:    '#101d34',
  border:  'rgba(255,255,255,0.06)',
  primary: '#00c896',
  blue:    '#3b82f6',
  danger:  '#ef4444',
  price:   '#4ade80',
  text:    '#e8f1ff',
  text2:   '#7a94c0',
  text3:   '#3a5070',
};

// ── Sub-components ────────────────────────────────────────────────────────────
function ImgPlaceholder({ emoji, gradient }) {
  return (
    <div style={{
      width: '100%', paddingTop: '64%', position: 'relative',
      background: gradient, borderRadius: '14px 14px 0 0', overflow: 'hidden',
    }}>
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        fontSize: 38, lineHeight: 1,
        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))',
      }}>{emoji}</span>
    </div>
  );
}

function ItemCard({ item, catName, qty, onAdd, onRemove }) {
  const inCart = qty > 0;
  return (
    <div style={{
      background: C.card, borderRadius: 16, overflow: 'hidden',
      border: `1px solid ${inCart ? C.primary + '45' : C.border}`,
      boxShadow: inCart ? `0 4px 18px ${C.primary}15` : 'none',
      display: 'flex', flexDirection: 'column',
      transition: 'border-color .2s, box-shadow .2s',
    }}>
      {item.imageUrl
        ? <img src={item.imageUrl} alt={item.name}
            style={{ width:'100%', aspectRatio:'5/3', objectFit:'cover', display:'block' }} />
        : <ImgPlaceholder emoji={catEmoji(catName)} gradient={pickGrad(item.name)} />
      }
      <div style={{ padding:'12px 12px 14px', flex:1, display:'flex', flexDirection:'column' }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.text, lineHeight:1.35, marginBottom:4,
          overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {item.name}
        </div>
        {item.description && (
          <div style={{ fontSize:11, color:C.text2, lineHeight:1.4, flex:1, marginBottom:6,
            overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
            {item.description}
          </div>
        )}
        {item.preparationMinutes > 0 && (
          <div style={{ fontSize:10, color:C.text3, marginBottom:8 }}>⏱ {item.preparationMinutes} dəq</div>
        )}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'auto' }}>
          <span style={{ fontSize:15, fontWeight:800, color:C.price }}>₼{Number(item.price).toFixed(2)}</span>
          {qty === 0
            ? <button onClick={() => onAdd(item)} style={{
                background:C.blue, color:'#fff', border:'none', borderRadius:9,
                width:32, height:32, fontSize:20, fontWeight:700, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>+</button>
            : <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <button onClick={() => onRemove(item.id)} style={{
                  background:`${C.danger}1a`, color:C.danger, border:`1px solid ${C.danger}28`,
                  borderRadius:7, width:28, height:28, fontSize:16, fontWeight:700, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>−</button>
                <span style={{ fontSize:15, fontWeight:800, color:C.primary, minWidth:14, textAlign:'center' }}>{qty}</span>
                <button onClick={() => onAdd(item)} style={{
                  background:C.blue, color:'#fff', border:'none',
                  borderRadius:7, width:28, height:28, fontSize:16, fontWeight:700, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>+</button>
              </div>
          }
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'16px' }}>
      <style>{`@keyframes sk{0%,100%{opacity:.35}50%{opacity:.7}}.sk{animation:sk 1.6s ease infinite;border-radius:12px;background:#111d34;}`}</style>
      <div className="sk" style={{ height:24, width:'52%', marginBottom:8 }} />
      <div className="sk" style={{ height:14, width:'28%', marginBottom:20 }} />
      <div className="sk" style={{ height:36, marginBottom:20, borderRadius:20 }} />
      <div className="sk" style={{ height:18, width:'32%', marginBottom:14 }} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {[...Array(6)].map((_,i) => <div key={i} className="sk" style={{ height:200 }} />)}
      </div>
    </div>
  );
}

function ErrorPage({ msg }) {
  return (
    <div style={{ background:C.bg, minHeight:'100vh', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center', padding:'24px', textAlign:'center' }}>
      <div style={{ fontSize:52, marginBottom:16 }}>😕</div>
      <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:8 }}>Menyu tapılmadı</div>
      <div style={{ fontSize:13, color:C.text2, maxWidth:240 }}>
        {msg || 'QR kodu etibarsız və ya menyu hələ hazır deyil'}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function MenuPage() {
  const { tableId } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activecat, setActivecat] = useState(null);
  const [cart, dispatch]      = useReducer(cartReducer, []);
  const [showCart, setShowCart] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [note, setNote]       = useState('');
  const [payMethod, setPayMethod] = useState('cash');

  const catRefs = useRef({});

  useEffect(() => {
    getTableMenu(tableId)
      .then(d => { setData(d); if (d.categories?.[0]) setActivecat(d.categories[0].id); })
      .catch(e => setError(e?.message || e?.error || 'Yüklənmə xətası'))
      .finally(() => setLoading(false));
  }, [tableId]);

  const getQty   = useCallback(id => cart.find(i => i.id === id)?.qty || 0, [cart]);
  const total    = cart.reduce((s, i) => s + Number(i.price) * i.qty, 0);
  const totalQty = cart.reduce((s, i) => s + i.qty, 0);
  const onAdd    = useCallback(item => dispatch({ type:'ADD', item }), []);
  const onRemove = useCallback(id   => dispatch({ type:'REMOVE', id }), []);

  const handleOrder = async () => {
    if (ordering || !cart.length) return;
    setOrdering(true);
    try {
      const order = await createOrder({
        tableId,
        notes: note.trim() || undefined,
        items: cart.map(i => ({ menuItemId: i.id, quantity: i.qty })),
        paymentMethod: payMethod,
      });
      dispatch({ type:'CLEAR' }); setShowCart(false); setNote('');
      const id = order?.id || order?.data?.id;
      if (id) navigate(`/order/${id}`);
    } catch (e) {
      alert(e?.message || e?.error || 'Sifariş uğursuz oldu. Yenidən cəhd edin.');
    } finally { setOrdering(false); }
  };

  const scrollTo = catId => {
    setActivecat(catId);
    catRefs.current[catId]?.scrollIntoView({ behavior:'smooth', block:'start' });
  };

  if (loading) return <LoadingSkeleton />;
  if (error || !data) return <ErrorPage msg={error} />;

  const { restaurant, table, categories = [] } = data;
  const visibleCats = categories.filter(c => (c.items || []).some(i => i.isAvailable !== false));

  return (
    <div style={{ background:C.bg, minHeight:'100vh',
      fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', color:C.text }}>

      {/* ── Sticky header + nav ── */}
      <div style={{ position:'sticky', top:0, zIndex:50, background:C.bg }}>
        <div style={{ padding:'16px 16px 12px',
          background:`linear-gradient(180deg,${C.surface} 0%,${C.bg} 100%)`,
          borderBottom:`1px solid ${C.border}` }}>
          <div style={{ maxWidth:640, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:800, letterSpacing:'-0.02em', color:C.text }}>
                {restaurant?.name || 'Restoran'}
              </div>
              <div style={{ fontSize:12, color:C.text2, marginTop:3 }}>
                📍 {table?.tableNumber ? `Masa ${table.tableNumber}` : 'Masa'}
                {restaurant?.address ? ` · ${restaurant.address}` : ''}
              </div>
            </div>
            <img src="/assets/logo-mark.png" alt="BG Sync" style={{ height:40, width:40, flexShrink:0 }} />
          </div>
        </div>

        {visibleCats.length > 1 && (
          <div style={{ borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', overflowX:'auto', gap:4, padding:'7px 12px',
              scrollbarWidth:'none', maxWidth:640, margin:'0 auto' }}>
              {visibleCats.map(cat => (
                <button key={cat.id} onClick={() => scrollTo(cat.id)} style={{
                  flexShrink:0, padding:'5px 13px',
                  background: activecat===cat.id ? `${C.primary}18` : 'transparent',
                  color: activecat===cat.id ? C.primary : C.text2,
                  border:`1px solid ${activecat===cat.id ? C.primary+'45' : 'transparent'}`,
                  borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer',
                  transition:'all .18s', whiteSpace:'nowrap',
                }}>
                  {catEmoji(cat.name)} {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Items ── */}
      <div style={{ maxWidth:640, margin:'0 auto', padding:'16px 14px 180px' }}>
        {visibleCats.map(cat => {
          const items = (cat.items || []).filter(i => i.isAvailable !== false);
          return (
            <div key={cat.id} ref={el => catRefs.current[cat.id]=el}
              style={{ marginBottom:32, scrollMarginTop:132 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8,
                fontSize:16, fontWeight:800, marginBottom:14, paddingTop:4 }}>
                <span>{catEmoji(cat.name)}</span>
                <span>{cat.name}</span>
                <span style={{ fontSize:12, fontWeight:500, color:C.text3 }}>({items.length})</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
                {items.map(item => (
                  <ItemCard key={item.id} item={item} catName={cat.name}
                    qty={getQty(item.id)} onAdd={onAdd} onRemove={onRemove} />
                ))}
              </div>
            </div>
          );
        })}
        {visibleCats.length === 0 && (
          <div style={{ textAlign:'center', padding:'80px 20px', color:C.text3 }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🍽</div>
            <div style={{ fontSize:15, fontWeight:600 }}>Menyu hələ əlavə edilməyib</div>
          </div>
        )}
      </div>

      {/* ── Cart bar ── */}
      <div style={{
        position:'fixed', bottom:0, left:0, right:0, zIndex:60,
        display:'flex', justifyContent:'center', padding:'0 14px 20px',
        transform: totalQty > 0 ? 'translateY(0)' : 'translateY(120px)',
        transition:'transform .38s cubic-bezier(.34,1.56,.64,1)',
        pointerEvents: totalQty > 0 ? 'auto' : 'none',
      }}>
        <button onClick={() => setShowCart(true)} style={{
          width:'100%', maxWidth:612,
          background:`linear-gradient(135deg,${C.primary} 0%,#00a878 100%)`,
          border:'none', borderRadius:18, padding:'14px 18px',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          cursor:'pointer',
          boxShadow:`0 8px 32px ${C.primary}40,0 2px 8px rgba(0,0,0,.4)`,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ background:'rgba(0,0,0,.22)', borderRadius:10,
              padding:'3px 9px', fontSize:13, fontWeight:800, color:'#fff' }}>{totalQty}</div>
            <span style={{ fontSize:14, fontWeight:700, color:'#fff' }}>Səbəti Gör</span>
          </div>
          <span style={{ fontSize:15, fontWeight:800, color:'#fff' }}>₼{total.toFixed(2)}</span>
        </button>
      </div>

      {/* ── Cart sheet ── */}
      {showCart && (
        <div
          style={{ position:'fixed', inset:0, zIndex:100,
            background:'rgba(0,0,0,.72)', backdropFilter:'blur(4px)',
            display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={e => e.target===e.currentTarget && setShowCart(false)}
        >
          <div style={{
            width:'100%', maxWidth:640,
            background:C.surface, borderRadius:'24px 24px 0 0',
            border:`1px solid ${C.border}`, borderBottom:'none',
            maxHeight:'88vh', display:'flex', flexDirection:'column',
            animation:'cartUp .28s ease',
          }}>
            {/* Handle + title */}
            <div style={{ padding:'10px 18px 0', flexShrink:0 }}>
              <div style={{ width:40, height:4, background:C.border, borderRadius:2, margin:'0 auto 14px' }} />
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                paddingBottom:14, borderBottom:`1px solid ${C.border}` }}>
                <div>
                  <div style={{ fontSize:17, fontWeight:800 }}>🛒 Səbət</div>
                  <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>{totalQty} məhsul</div>
                </div>
                <button onClick={() => setShowCart(false)} style={{
                  background:'rgba(255,255,255,.07)', border:'none', borderRadius:10,
                  width:34, height:34, fontSize:16, color:C.text2, cursor:'pointer',
                }}>✕</button>
              </div>
            </div>

            {/* Items */}
            <div style={{ overflowY:'auto', flex:1, padding:'10px 18px' }}>
              {cart.map(item => (
                <div key={item.id} style={{
                  display:'flex', alignItems:'center', gap:10,
                  paddingBottom:14, marginBottom:14, borderBottom:`1px solid ${C.border}`,
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:3,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
                    <div style={{ fontSize:13, color:C.price, fontWeight:700 }}>
                      ₼{(Number(item.price)*item.qty).toFixed(2)}
                      {item.qty>1 && <span style={{ fontSize:11, color:C.text3, fontWeight:500 }}>
                        {' '}(₼{Number(item.price).toFixed(2)}×{item.qty})
                      </span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    <button onClick={() => onRemove(item.id)} style={{
                      background:`${C.danger}18`, color:C.danger, border:`1px solid ${C.danger}28`,
                      borderRadius:7, width:28, height:28, fontSize:16, fontWeight:700,
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                    }}>−</button>
                    <span style={{ fontSize:14, fontWeight:800, color:C.primary, minWidth:14, textAlign:'center' }}>{item.qty}</span>
                    <button onClick={() => onAdd(item)} style={{
                      background:C.blue, color:'#fff', border:'none',
                      borderRadius:7, width:28, height:28, fontSize:16, fontWeight:700,
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                    }}>+</button>
                  </div>
                </div>
              ))}

              <div style={{ marginTop:4, marginBottom:4 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text2, marginBottom:8 }}>📝 Əlavə qeyd</div>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value.slice(0,250))}
                  placeholder="Məs: Az duz, allerji..."
                  rows={2}
                  style={{ width:'100%', background:C.card, border:`1px solid ${C.border}`,
                    borderRadius:12, padding:'10px 12px',
                    fontSize:13, color:C.text, resize:'none',
                    fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding:'14px 18px 28px', borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <span style={{ fontSize:14, color:C.text2, fontWeight:600 }}>Cəmi:</span>
                <span style={{ fontSize:20, fontWeight:800, color:C.price }}>₼{total.toFixed(2)}</span>
              </div>

              {/* Payment method selection */}
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Ödəniş üsulu</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[{k:'cash',l:'💵 Nağd'},{k:'card',l:'💳 Kart'},{k:'online',l:'📱 Online'}].map(m => (
                    <button key={m.k} onClick={() => setPayMethod(m.k)} style={{
                      flex:1, padding:'9px 6px', borderRadius:10, border:`2px solid ${payMethod===m.k ? C.primary : C.border}`,
                      background: payMethod===m.k ? `${C.primary}18` : C.card,
                      fontSize:11, fontWeight:700, color: payMethod===m.k ? C.primary : C.text2,
                      cursor:'pointer', transition:'all .15s',
                    }}>{m.l}</button>
                  ))}
                </div>
              </div>

              <button onClick={handleOrder} disabled={ordering} style={{
                width:'100%', padding:'15px',
                background: ordering ? C.text3 : `linear-gradient(135deg,${C.primary} 0%,#00a878 100%)`,
                border:'none', borderRadius:15,
                fontSize:15, fontWeight:800, color:'#fff',
                cursor: ordering ? 'not-allowed' : 'pointer',
                boxShadow: ordering ? 'none' : `0 6px 20px ${C.primary}35`,
                transition:'background .2s',
              }}>
                {ordering ? '⟳ Göndərilir...' : `🍽 Sifariş Ver · ₼${total.toFixed(2)}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Brand footer */}
      <div style={{ position:'fixed', bottom:0, left:0, right:0, textAlign:'center', padding:'6px', pointerEvents:'none', zIndex:1 }}>
        <span style={{ fontSize:10, color:C.text3, fontWeight:600 }}>
          Baku Gate · <span style={{ color:C.primary }}>by.Elvin</span>
        </span>
      </div>

      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:0;height:0}
        body{background:${C.bg}}
        button{font-family:inherit}
        @keyframes cartUp{from{transform:translateY(100%);opacity:.6}to{transform:translateY(0);opacity:1}}
      `}</style>
    </div>
  );
}
